import Calendar from 'react-calendar';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  fetchRoomAvailability,
  fetchCustomerData,
} from '../../utils/functions';
import { Order } from '../../types/customer';
import { Room, RoomAvailability } from '../../types/accommodation';
import Spinner from '../../components/Spinner';

interface Props {
  activeRoom: Room;
  calendarTopElements: (roomType: string) => JSX.Element;
  isTabletOrMobile: boolean;
  dateFormatLanguage: string;
  calendarDateFormatOptions: Intl.DateTimeFormatOptions;
  startDate: Date;
  idToken: string;
}

function checkRoomAvailability(
  params: { date: Date; view: string; activeStartDate: Date },
  roomAvailabilities: RoomAvailability[],
  ownOrders: Order[] | null
) {
  const className = 'react-calendar__month-view__days__day';
  const { date, view } = params;

  if (roomAvailabilities && view === 'month') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (today.getTime() > date.getTime()) {
      return className + '--past';
    }

    // Check users own reservations.
    if (ownOrders && ownOrders.length > 0) {
      for (const order of ownOrders) {
        if (!order.Confirmed) {
          continue;
        }
        const arrivalDate = new Date(order.DateTimeStart);
        const departureDate = new Date(order.DateTimeEnd);
        // Departure date shouldn't be shown as booked in the calendar.
        if (
          departureDate.toDateString() !== date.toDateString() &&
          arrivalDate <= date &&
          departureDate >= date
        ) {
          return className + '--own-reservation';
        }
      }
    }

    // Check availability in general, if the user doesn't have personal
    // reservation on the date.
    const roomData = roomAvailabilities.find(
      (availability) =>
        new Date(availability.Date).toDateString() === date.toDateString()
    );

    if (roomData && !roomData?.IsAvailable) {
      return className + '--blocked';
    }
  }
}

function RoomCalendar(props: Props): JSX.Element {
  const [roomAvailabilities, setRoomAvailabilities] = useState<{
    [key: string]: RoomAvailability[];
  }>();
  const [fetchingRoomAvailabilities, setFetchingRoomAvailabilities] =
    useState(true);
  const [fetchingOrders, setFetchingOrders] = useState(true);
  const [orders, setOrders] = useState<Order[]>();
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const { t, i18n } = useTranslation('owned_rooms');

  useEffect(() => {
    async function getCustomerData() {
      const data = await fetchCustomerData(props.idToken);

      if (data && data.orders) {
        setOrders(data.orders);
      }
      setFetchingOrders(false);
    }

    if (props.idToken) {
      getCustomerData();
    }
  }, [props.idToken, i18n.language]);

  useEffect(() => {
    async function getRoomAvailabilities() {
      if (props.activeRoom) {
        setErrorMessage(null);
        setFetchingRoomAvailabilities(true);
        const endDate = new Date(props.startDate.getTime());
        endDate.setFullYear(endDate.getFullYear() + 2);
        // Set to last day of month.
        endDate.setDate(0);
        const searchStartDate = new Date(props.startDate.getTime());

        const roomData = await fetchRoomAvailability({
          token: props.idToken,
          startDate: searchStartDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          roomId: props.activeRoom.roomId,
        });

        if (roomData) {
          const roomAvailabilityData = roomAvailabilities ?? {};
          roomAvailabilityData[props.activeRoom.roomId] =
            roomData.availabilities;
          setRoomAvailabilities(roomAvailabilityData);
        } else {
          setErrorMessage(t('Error fetching availabilities for the room.'));
        }
        setFetchingRoomAvailabilities(false);
      }
    }

    // Check if there's already data for the room.
    if (
      props.activeRoom &&
      (!roomAvailabilities || !roomAvailabilities[props.activeRoom.roomId])
    ) {
      getRoomAvailabilities();
    }
  }, [props.activeRoom]);

  return (
    <div>
      {fetchingRoomAvailabilities && (
        <div className='my-20'>
          <Spinner />
        </div>
      )}

      {!fetchingOrders && !orders && (
        <p className='text-red-500 my-8'>{t("Couldn't fetch your orders.")}</p>
      )}

      {!fetchingRoomAvailabilities && errorMessage && (
        <p className='text-red-500 my-20'>{errorMessage}</p>
      )}

      {!fetchingRoomAvailabilities &&
        !fetchingOrders &&
        !errorMessage &&
        roomAvailabilities && (
          <>
            {props.calendarTopElements('room')}

            <Calendar
              formatShortWeekday={(locale, date) =>
                date.toLocaleDateString(
                  props.dateFormatLanguage,
                  props.calendarDateFormatOptions
                )
              }
              activeStartDate={props.startDate}
              showNavigation={false}
              showWeekNumbers={true}
              view={'month'}
              tileDisabled={() => {
                return true;
              }}
              showDoubleView={!props.isTabletOrMobile}
              tileClassName={(params) =>
                checkRoomAvailability(
                  params,
                  roomAvailabilities[props.activeRoom.roomId],
                  orders
                    ? orders.filter(
                        (order) =>
                          order.RoomId === parseInt(props.activeRoom.roomId)
                      )
                    : null
                )
              }
            />
          </>
        )}
    </div>
  );
}

export default RoomCalendar;
